'use client'

import { Square, CheckSquare } from 'lucide-react'

export interface Chapter {
  id: string
  title: string
  subtitle: string
  count: number
}

interface TOCProps {
  chapters: Chapter[]
  visibleChapterIds: Set<string>
  activeChapter: string
  onToggleSelectAll: () => void
  onToggleChapterVisibility: (id: string) => void
  onScrollToChapter: (id: string) => void
}

export default function TOC({
  chapters,
  visibleChapterIds,
  activeChapter,
  onToggleSelectAll,
  onToggleChapterVisibility,
  onScrollToChapter,
}: TOCProps) {
  const isAllSelected = visibleChapterIds.size === chapters.length

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm flex flex-col flex-1">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between shrink-0">
        <h4 className="text-[14px] font-black text-slate-800 uppercase tracking-tight">
          Kết quả tìm kiếm
        </h4>
        <div className="text-[11px] font-black text-slate-400 uppercase">
          {chapters.length} chương
        </div>
      </div>

      {/* Select All/Deselect All Toggle */}
      <div className="px-5 py-2.5 bg-white border-b border-slate-100 flex items-center shrink-0">
        <button
          onClick={onToggleSelectAll}
          className={`flex items-center gap-1.5 text-[12px] font-bold transition-colors ${isAllSelected
            ? 'text-slate-500 hover:text-red-500'
            : 'text-primary hover:text-primary-dark'
            }`}
        >
          {isAllSelected ? (
            <>
              <Square size={14} strokeWidth={2.5} /> Bỏ chọn tất cả
            </>
          ) : (
            <>
              <CheckSquare size={14} strokeWidth={2.5} /> Chọn tất cả
            </>
          )}
        </button>
      </div>

      {/* Chapter List */}
      <nav className="flex flex-col overflow-y-auto custom-scrollbar flex-1">
        {chapters.map((chapter) => {
          const isActive = activeChapter === chapter.id && visibleChapterIds.has(chapter.id)
          const isVisible = visibleChapterIds.has(chapter.id)

          return (
            <div
              key={chapter.id}
              className={`flex items-center gap-3 px-4 py-3 border-l-[3px] transition-all border-b border-slate-50 last:border-b-0 ${isActive
                ? 'bg-primary/5 border-primary'
                : 'border-transparent hover:bg-slate-50/40'
                }`}
            >
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={isVisible}
                onChange={() => onToggleChapterVisibility(chapter.id)}
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20 cursor-pointer shrink-0"
              />

              {/* Chapter Info - Clickable */}
              <button
                onClick={() => onScrollToChapter(chapter.id)}
                className="flex items-center justify-between flex-1 text-left min-w-0"
              >
                <div className="flex flex-col gap-0 min-w-0 pr-2">
                  <span
                    className={`text-[10px] font-black uppercase tracking-tight ${isActive ? 'text-primary' : 'text-slate-400'
                      }`}
                  >
                    {chapter.title}
                  </span>
                  <span
                    className={`text-[13px] font-medium leading-tight truncate ${isActive ? 'text-slate-900' : 'text-slate-600'
                      }`}
                  >
                    {chapter.subtitle}
                  </span>
                </div>

                {/* Count Badge */}
                <div
                  className={`text-[13px] font-semibold px-2 py-0.5 rounded-md shrink-0 ${isActive ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'
                    }`}
                >
                  {chapter.count}
                </div>
              </button>
            </div>
          )
        })}
      </nav>
    </div>
  )
}
